import { PlayerPublic } from "@aura-frontend/omnibus-az-client";
import { SortingState, Updater } from "@tanstack/react-table";
import { cn, Table, TableProps } from "@win-reality/react-ui";
import { useCallback } from "react";

import { usePlayerColumns } from "./PlayersTable.columns";

type PlayersTableProps = {
  data: PlayerPublic[];
  fetchNextPage: () => void;
  hasNextPage: boolean;
  pageSize: number;
  isFetchingNextPage?: boolean;
  shouldRenderActions: boolean;
  sortingState?: SortingState;
  onSortingChange?: (updater: Updater<SortingState>) => void;
  size?: TableProps<PlayerPublic>["size"];
  disableSorting?: boolean;
  showJerseyNumber?: boolean;
};

function PlayersTable({
  data,
  shouldRenderActions,
  pageSize,
  sortingState,
  onSortingChange,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  size = "default",
  disableSorting = true,
  showJerseyNumber = false,
}: PlayersTableProps) {
  const handleSortingChange = useCallback(
    (updater: Updater<SortingState>) => {
      onSortingChange?.((oldSortingState) =>
        typeof updater === "function" ? updater(oldSortingState) : updater,
      );
    },
    [onSortingChange],
  );

  const { playersColumns } = usePlayerColumns({
    shouldRenderActions,
    disableSorting,
    showJerseyNumber,
  });

  const isCompact = size === "compact";

  return (
    <Table
      enableHorizontalScroll
      size={size}
      cellClassName={isCompact ? "!bg-transparent" : ""}
      rowClassName={cn(
        !isCompact && "shadow-glow-tiny rounded",
        isCompact && "hover:bg-[hsla(218,100%,52%,0.1)]",
      )}
      rowsWrapperClassName={cn(!isCompact && "gap-y-4")}
      tableWrapperClassName={cn(
        "overflow-auto",
        !isCompact && "px-5 py-1",
        isCompact && "px-2",
      )}
      variant="infinite-scroll"
      hasMore={hasNextPage}
      isFetching={isFetchingNextPage}
      paginationState={{
        pageSize,
        pageIndex: 0,
      }}
      columns={playersColumns}
      data={data}
      sortingState={sortingState}
      onSortingChange={handleSortingChange}
      onEndReached={() => {
        if (hasNextPage && !isFetchingNextPage && fetchNextPage) {
          fetchNextPage();
        }
      }}
    />
  );
}

export { PlayersTable };
